import type { LocationQuery, RouteLocationRaw } from 'vue-router'
import type { FeedItem, FeedVideo } from '@/entities/feed'
import { toFeedCardModel, type FeedCardModel } from '@/components/feed/feed-card.model'
import { SummaryVisibility } from '@/composables/enums/summary-visibility'

export type ListingKind = 'summary' | 'vacancy'

const KINDS: ListingKind[] = ['summary', 'vacancy']
const ID_PATTERN = /^[0-9a-f-]{36}$/i

/** Canonical full page (direct/shared links, SEO, «Открыть в новой вкладке»). */
export function listingPagePath(kind: ListingKind, id: string): string {
  return kind === 'vacancy' ? `/vacancies/${id}` : `/summaries/${id}`
}

function withoutPreview(query: LocationQuery): LocationQuery {
  const rest = { ...query }
  for (const kind of KINDS) delete rest[kind]
  delete rest.previewResponse
  return rest
}

/**
 * Preview state lives in the current URL (`?summary=<id>` / `?vacancy=<id>`)
 * so the list underneath stays mounted with its scroll and loaded pages,
 * Back closes the preview, and a copied URL reopens it.
 */
export function useListingPreview() {
  const route = useRoute()
  const router = useRouter()

  const target = computed<{ kind: ListingKind, id: string, responseId?: string } | null>(() => {
    for (const kind of KINDS) {
      const id = route.query[kind]
      if (typeof id === 'string' && ID_PATTERN.test(id)) {
        const response = route.query.previewResponse
        return { kind, id, ...(typeof response === 'string' && ID_PATTERN.test(response) ? { responseId: response } : {}) }
      }
    }
    return null
  })

  function previewLocation(kind: ListingKind, id: string, responseId?: string): RouteLocationRaw {
    return { path: route.path, hash: route.hash, query: { ...withoutPreview(route.query), [kind]: id, ...(responseId ? { previewResponse: responseId } : {}) } }
  }

  function open(kind: ListingKind, id: string, responseId?: string) {
    return router.push(previewLocation(kind, id, responseId))
  }

  function close() {
    const base = router.resolve({ path: route.path, hash: route.hash, query: withoutPreview(route.query) }).fullPath
    // Opened from this list: step back so Back/forward history stays clean.
    if (import.meta.client && window.history.state?.back === base) return router.back()
    return router.replace(base)
  }

  return { target, previewLocation, open, close }
}

type ApiQuestion = { id: string, question: string, position?: number, answer?: { src?: string | null, duration?: number | null } | null }
type ApiListing = Record<string, any> & { id: string, questions?: ApiQuestion[] }

export type ListingPreviewData = {
  item: FeedItem
  favoriteId: string | null
  responseId: string | null
  /** Nobody can respond or invite: a draft, archived or expired vacancy, a draft or hidden resume. */
  unpublished: boolean
  /** Why the listing is not public; only its owner or a response participant sees such a listing. */
  notice: string | null
  /** Unix seconds: publication of a vacancy, last update of a resume. */
  date: number | null
}

function videosOf(listing: ApiListing): FeedVideo[] {
  return (listing.questions ?? [])
    .filter(question => typeof question.answer?.src === 'string' && question.answer.src.length > 0)
    .map((question, index) => ({
      id: question.id,
      src: question.answer!.src as string,
      duration: question.answer?.duration ?? 0,
      position: question.position ?? index,
      question: question.question,
      hls: null,
      poster: null,
    }))
}

const city = (value: any) => ({ id: value?.id ?? 0, title: value?.title ?? 'Город не указан', titleIn: value?.titleIn ?? null, alias: value?.alias ?? '' })
const experience = (value: any) => value?.title || value?.name ? { value: value.value ?? value.id ?? 0, title: value.title ?? value.name } : null

/**
 * Maps the public `GET /summaries/{id}` / `GET /vacancies/{id}` payload to the
 * feed item shape so the preview reuses the feed card, details and actions.
 */
function toListingPreview(kind: ListingKind, listing: ApiListing): ListingPreviewData {
  const base = { id: listing.id, position: 0, score: null, itemRevision: '', matchedVacancyId: null, videos: videosOf(listing) }
  const schedules = (listing.schedules ?? []).map((item: any) => ({ type: item.type ?? 0, name: item.name ?? '' }))
  const item: FeedItem = kind === 'vacancy'
    ? {
        ...base,
        type: 'vacancy',
        summary: null,
        vacancy: {
          title: listing.title || listing.profession?.title || 'Вакансия',
          description: listing.description ?? null,
          city: city(listing.city),
          company: { id: listing.company?.id ?? '', name: listing.company?.name ?? 'Компания', logo: listing.company?.logo ?? null },
          profession: { id: listing.profession?.id ?? 0, title: listing.profession?.title ?? '' },
          salary: { min: listing.salary?.min ?? null, max: listing.salary?.max ?? null },
          address: listing.address ?? null,
          schedules,
          experience: experience(listing.experience) ?? { value: 0, title: 'Не указан' },
          typeEmployment: experience(listing.typeEmployment) ?? { value: 0, title: 'Не указан' },
          hasContacts: Boolean(listing.hasContacts),
        },
      }
    : {
        ...base,
        type: 'summary',
        vacancy: null,
        summary: {
          title: listing.title || (listing.professions ?? []).map((profession: any) => profession.title).join(', ') || 'Резюме',
          city: city(listing.city),
          candidate: { id: listing.client?.id ?? '', name: listing.client?.name ?? 'Кандидат', surname: listing.client?.surname ?? null, photo: listing.client?.photo ?? null },
          professions: (listing.professions ?? []).map((profession: any) => ({ id: profession.id ?? 0, title: profession.title ?? '' })),
          schedules,
          age: typeof listing.age === 'number' ? listing.age : null,
          experienceInProfession: experience(listing.experienceInProfession),
          hasContacts: Boolean(listing.hasContacts),
        },
      }

  return {
    item,
    favoriteId: listing.favorite?.id ?? null,
    responseId: listing.response?.id ?? null,
    ...availability(kind, listing),
    date: (kind === 'vacancy' ? listing.publicationDate : listing.updated) ?? null,
  }
}

function availability(kind: ListingKind, listing: ApiListing): Pick<ListingPreviewData, 'unpublished' | 'notice'> {
  if (kind === 'vacancy') {
    if (listing.isDraft) return { unpublished: true, notice: 'Черновик: вакансия ещё не опубликована' }
    if (listing.isArchived) return { unpublished: true, notice: 'Вакансия в архиве' }
    if (listing.isPublished === false) return { unpublished: true, notice: 'Вакансия снята с публикации' }
    return { unpublished: false, notice: null }
  }
  if (listing.isDraft) return { unpublished: true, notice: 'Черновик: резюме ещё не опубликовано' }
  if (listing.visibility === SummaryVisibility.Invisible) return { unpublished: true, notice: 'Резюме скрыто: его видите только вы' }
  return { unpublished: false, notice: null }
}

/** The page details: the feed card plus the vacancy fields the feed leaves out. */
export function toListingCardModel(item: FeedItem): FeedCardModel {
  const card = toFeedCardModel(item)
  if (item.type !== 'vacancy') return card
  const vacancy = item.vacancy
  return {
    ...card,
    details: [
      { label: 'Город', value: vacancy.city.title },
      { label: 'Профессия', value: vacancy.profession.title || 'Не указана' },
      { label: 'Опыт работы', value: vacancy.experience.title },
      { label: 'Тип занятости', value: vacancy.typeEmployment.title },
      { label: 'График', value: vacancy.schedules.map(({ name }) => name).join(', ') || 'Не указан' },
      ...(vacancy.address ? [{ label: 'Адрес', value: vacancy.address }] : []),
    ],
  }
}

export type ListingLoadResult =
  | { ok: true, data: ListingPreviewData }
  | { ok: false, statusCode: number, message: string }

export async function loadListing(kind: ListingKind, id: string): Promise<ListingLoadResult> {
  const resource = kind === 'vacancy' ? 'vacancies' : 'summaries'
  const { data, error } = await mainFetch(`${resource}/${id}`, kind === 'vacancy'
    ? { query: { 'get[questions]': true, 'get[getResponse]': true } }
    : {})
  const listing = data.value?.[kind]
  if (!listing || typeof listing.id !== 'string') {
    return { ok: false, statusCode: error.value?.statusCode ?? 404, message: apiErrorMessage(error.value, 'Публикация не найдена') }
  }
  return { ok: true, data: toListingPreview(kind, listing) }
}
